import { type Brand, make } from "ts-brand";
import { z } from "zod";

import { DocumentId, CategorySchema, DocumentStatusSchema, SenderSchema } from "./document";
import { MandateId, OrganizationId, PersonId } from "./mandate";
import { PageRequestSchema } from "./pagination";
import { TaskListId } from "./task-list";

/**
 * InboxEntry domain model (Core 1 — symmetric inbox). An entry is the group-level
 * signal that the other side did something the owning organisation should see.
 * It is a discriminated union over `kind`:
 *
 * - `document` — a customer-submitted Document (Trexpert-Org inbox, S3) or a
 *   Trexpert-provided Document (Customer-Org inbox, S1); references the Document.
 * - `task-list` — a TaskList sent to the customer (Customer-Org inbox, S5);
 *   references the TaskList.
 *
 * Read state is group-scoped: one member opening it sets `readState = "read"`
 * for the whole organisation — no user reference (decision D1, gate P3-G2). See
 * docs/stories/impl-posteingang.
 *
 * The transition is reversible: a trustee may mark handled entries `read → unread`
 * again (group-scoped, Sammelaktion «Als ungelesen markieren», plan
 * treuhaender-sammelaktionen Slice 2). This reverses D1's original one-way
 * constraint — a deliberate divergence from the oracle's one-way `markiereGelesen`,
 * recorded 2026-07-28 (Oracle governance). See {@link markInboxRead} and
 * {@link markInboxUnread}.
 */

export type InboxEntryId = Brand<string, "InboxEntryId">;
export const InboxEntryId = make<InboxEntryId>();

/**
 * Group read state. `unread → read` (open) and `read → unread` (trustee
 * Sammelaktion) are both idempotent. See {@link markInboxRead} and
 * {@link markInboxUnread}.
 */
export const INBOX_READ_STATES = ["unread", "read"] as const;
export type InboxReadState = (typeof INBOX_READ_STATES)[number];

export const InboxReadStateSchema = z.enum(INBOX_READ_STATES);

/** Fields shared by every InboxEntry, regardless of what it references. */
const InboxEntryBaseSchema = z.object({
    id: z.string().min(1).transform(InboxEntryId),
    mandateId: z.string().min(1).transform(MandateId),
    ownerOrgId: z.string().min(1).transform(OrganizationId),
    /** Who originated this entry: `"TRUSTEE"` for Trexpert-provided docs and task-lists; `"CUSTOMER"` for submitted docs. */
    sender: SenderSchema,
    /** When the originating action happened (submit / provide / send). */
    submittedAt: z.string().min(1),
    /** Who performed it (submitter / provider / sender). */
    submittedBy: z.string().min(1).transform(PersonId),
    /** Display name of the person who performed the action (for UI). */
    submittedByName: z.string().min(1),
    readState: InboxReadStateSchema,
});

/** An entry referencing a Document (customer submit or Trexpert provide). */
export const DocumentInboxEntrySchema = InboxEntryBaseSchema.extend({
    kind: z.literal("document"),
    documentId: z.string().min(1).transform(DocumentId),
    fileName: z.string().min(1),
    category: CategorySchema,
    /**
     * The referenced Document's lifecycle status at entry creation. Lets the
     * Posteingang flag a four-eyes draft as «In Review» (S1 review flow) so a
     * second clerk can pick it up and open the Mandate's file view to accept it.
     */
    status: DocumentStatusSchema,
    /**
     * The category's label as the owning Mandate currently spells it. A **read
     * projection**, not stored state: the service fills it when listing, which is
     * why it is optional — an entry on its way *into* the repository has no label
     * to carry, and inventing one at every creation site is what would make the
     * label a second source of truth. Absent means «fall back to the raw id».
     */
    categoryLabel: z.string().min(1).optional(),
});
export type DocumentInboxEntry = z.infer<typeof DocumentInboxEntrySchema>;

/** An entry referencing a sent TaskList (S5). */
export const TaskListInboxEntrySchema = InboxEntryBaseSchema.extend({
    kind: z.literal("task-list"),
    taskListId: z.string().min(1).transform(TaskListId),
    title: z.string().min(1),
});
export type TaskListInboxEntry = z.infer<typeof TaskListInboxEntrySchema>;

export const InboxEntrySchema = z.discriminatedUnion("kind", [
    DocumentInboxEntrySchema,
    TaskListInboxEntrySchema,
]);

export type InboxEntry = z.infer<typeof InboxEntrySchema>;

/**
 * Boundary query for the paginated Posteingang (replaces the old `listInbox`
 * fetch-everything). `readState` is the «Ungelesen»/«Alle» tab (omit for all);
 * `mandateId` and `category` are the filter dropdowns. A `category` filter
 * implies document entries only — task-list entries carry no category.
 */
export const InboxQuerySchema = PageRequestSchema.extend({
    readState: InboxReadStateSchema.optional(),
    mandateId: z.string().min(1).transform(MandateId).optional(),
    category: CategorySchema.optional(),
});
export type InboxQuery = z.infer<typeof InboxQuerySchema>;

/**
 * The filters the Posteingang's category options are computed under — the list's
 * own, minus the category. Options from an unfiltered set let the user pick a
 * category the current tab or Mandate has no entry for.
 */
export const InboxCategoriesInputSchema = z.object({
    readState: InboxReadStateSchema.optional(),
    mandateId: z.string().min(1).transform(MandateId).optional(),
});
export type InboxCategoriesInput = z.infer<typeof InboxCategoriesInputSchema>;

/**
 * The `unread → read` transition (decision D1, gate P3-G2), triggered when a
 * group member opens the entry. Idempotent: opening an already-read entry keeps
 * its current state. Reversed by {@link markInboxUnread}.
 */
export const markInboxRead = <T extends { readState: InboxReadState }>(entry: T): T => {
    if (entry.readState === "read") {
        return entry;
    }
    return { ...entry, readState: "read" };
};

/**
 * The `read → unread` transition — the group-scoped counterpart to
 * {@link markInboxRead}, letting a trustee mark handled entries unread again
 * (Sammelaktion «Als ungelesen markieren», plan treuhaender-sammelaktionen).
 * Idempotent: marking an already-unread entry keeps its current state. Reverses
 * D1's original one-way constraint (recorded 2026-07-28, Oracle governance).
 */
export const markInboxUnread = <T extends { readState: InboxReadState }>(entry: T): T => {
    if (entry.readState === "unread") {
        return entry;
    }
    return { ...entry, readState: "unread" };
};

/** Input for the bulk «Als ungelesen markieren» Sammelaktion (a list of entries). */
export const MarkInboxEntriesUnreadInputSchema = z.object({
    inboxEntryIds: z.array(z.string().min(1).transform(InboxEntryId)).min(1),
});
export type MarkInboxEntriesUnreadInput = z.infer<typeof MarkInboxEntriesUnreadInputSchema>;
